import { SyntheticEvent, useMemo, useState } from 'react';
import { Checkbox, Modal } from 'antd';
import JoinEventTable, { WrestlerDivision, WrestlerWeightClass } from './JoinEventTable';
import { EventWithMembership } from '@modules/supabase/events';
import useUser from '@hooks/useUser';
import { Athlete, DIVISION_STYLE, STATUS, TEAM_ROLE } from '@type/db-types';
import { log } from '@utils/logging';
import { stringFromError } from '@utils/utils';
import { Alert, Box, Button, FormHelperText, Stack, Tooltip, Typography } from '@mui/material';
import { Launch } from '@mui/icons-material';
import LoadingButton from '@atoms/LoadingButton';
import { calculateAge } from '@utils/date-utils';
import { getDivisionByAge } from '@utils/shared-utils';
import usePayment from '@hooks/usePayment';
import { SignUp } from '@type/api-types';
import { toast } from 'react-toastify';
import useTeams from '@hooks/useTeams';
import useModalControl from '@hooks/useModalControl';

// initialize divisions for all athletes
const initializeAthletesDivision = (
    athletes: Athlete[],
    event: EventWithMembership,
): WrestlerDivision[] => {
    return athletes.map((athlete) => {
        const selectedDivision = getDivisionByAge(calculateAge(athlete.birthday), event);
        return {
            athleteId: athlete.id,
            divisionId: selectedDivision?.id || '',
        };
    });
};

// NOTE: REMOVED FOR NOW - force user to select weight class for each athlete
// initialize weight classes for all athletes based on current division
// const initializeAthletesWeightClass = (
//     athletes: Athlete[],
//     event: EventWithMembership,
//     athletesDivision: WrestlerDivision[],
// ) => {
//     return athletes.map((athlete) => {
//         const wrestlerDivision = athletesDivision.find((div) => div.athleteId === athlete.id);
//         const selectedDivision = event.divisions.find(
//             (div) => div.id === wrestlerDivision?.divisionId,
//         );
//         // get weight class based on age and weight
//         const selectedClass = selectedDivision?.classes
//             ? getClassByAge(athlete.weight, selectedDivision.classes)
//             : null;
//         return {
//             athleteId: athlete.id,
//             weightClassId: selectedClass?.id || '',
//         };
//     });
// };

interface Props {
    event: EventWithMembership;
    onClose: (e: SyntheticEvent) => void;
}

export default function JoinEventsForm(props: Props) {
    const { event, onClose } = props;
    const { loading: userLoading, athletes: children } = useUser();
    const { athletes: teamAthletes, role } = useTeams();
    const isCoach = [TEAM_ROLE.COACH, TEAM_ROLE.OWNER].includes(role as TEAM_ROLE);
    const { setShowAddTeamMember, setShowAddChildModal } = useModalControl();

    // union of athletes and team members
    const athletes = useMemo(() => {
        // const athleteIds =
        const childrenAthletes: Athlete[] = children.map((a) => ({
            ...a,
            id: a.athleteId,
            status: a.userStatus,
        }));

        const uniqueTeamAthletes = (teamAthletes || [])?.filter(
            (id) => !childrenAthletes.find((c) => c.id === id.id),
        );
        return [...childrenAthletes, ...uniqueTeamAthletes];
    }, [children, teamAthletes]);

    const [error, setError] = useState<string>('');
    const [submitLoading, setSubmitLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<Athlete[]>([]);
    const [athletesDivision, setAthletesDivision] = useState<WrestlerDivision[]>(
        initializeAthletesDivision(athletes, event),
    );
    const [athletesWeightClass, setAthletesWeightClass] = useState<WrestlerWeightClass[]>([]);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [selectWavier, setSelectWavier] = useState(false);
    const { processEventPayment } = usePayment();

    const areAllSelectedRowsValid = useMemo(() => {
        const areAllSelectedRowsValid = selectedRows.every((row) => {
            const divisionSelected = athletesDivision.find(
                (div) => div.athleteId === row.id && div.divisionId,
            );
            const currentDivision = event.divisions.find(
                (div) => div.id === divisionSelected?.divisionId,
            );
            const weightClassSelected =
                currentDivision?.bracketStyle === DIVISION_STYLE.MADISON_SYSTEM ||
                athletesWeightClass.some((cls) => cls.athleteId === row.id && cls.weightClassId);
            return divisionSelected && weightClassSelected;
        });

        return areAllSelectedRowsValid;
    }, [selectedRows, athletesDivision, event.divisions, athletesWeightClass]);

    const handleBulkSubmit = () => {
        try {
            setSubmitLoading(true);
            setError('');
            const signUps: SignUp[] = selectedRows.map((a) => {
                const weightClass = athletesWeightClass.find((item) => item.athleteId === a.id);
                const division = athletesDivision.find((item) => item.athleteId === a.id);
                const athlete: SignUp['athlete'] = {
                    ...a,
                    id: a.id,
                    status: a.status,
                };
                return {
                    athlete,
                    classId: weightClass?.weightClassId || null,
                    divisionId: division?.divisionId || null,
                    eventId: event.id,
                    paymentId: '', // TODO: Add paymentId
                    status: STATUS.PENDING,
                    userId: a.id,
                };
            });

            toast
                .promise(
                    processEventPayment(signUps, (err) => {
                        setError(err);
                        toast.error(err);
                        setSubmitLoading(false);
                    }),
                    {
                        // pending: 'Processing payment',
                        // success: 'Payment successful',
                        error: 'Payment failed',
                    },
                )
                .then((response) => {
                    console.log('[processEventPayment] Payment response:', response);
                });
        } catch (error) {
            log(`[handleBulkSubmit]: ${stringFromError(error)}`, error);
            setSubmitLoading(false);
        }
    };

    const disableSubmit = !selectWavier || !selectedRows.length || !areAllSelectedRowsValid;

    if (!athletes.length) {
        return (
            <Stack spacing={2}>
                <Typography variant="h6">No athletes found</Typography>
                <Button
                    onClick={() =>
                        isCoach ? setShowAddTeamMember(true) : setShowAddChildModal(true)
                    }
                >
                    Add Wrestler
                </Button>
            </Stack>
        );
    }

    return (
        <>
            <JoinEventTable
                athletes={[...athletes]}
                loading={userLoading}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                setAthletesDivision={(division) =>
                    setAthletesDivision((prev) => {
                        const prevAthleteDivision = prev.find(
                            (item) => item.athleteId === division.athleteId,
                        );
                        if (prevAthleteDivision) {
                            // Remove weight class if division is changed
                            setAthletesWeightClass((prev) => {
                                return prev.filter((item) => item.athleteId !== division.athleteId);
                            });
                        }

                        const otherPickedDivisions = prev.filter(
                            (item) => item.athleteId !== division.athleteId,
                        );
                        return [...otherPickedDivisions, division];
                    })
                }
                setAthletesWeightClass={(weightClass) =>
                    setAthletesWeightClass((prev) => {
                        const otherAthletes = prev.filter(
                            (item) => item.athleteId !== weightClass.athleteId,
                        );
                        return [...otherAthletes, weightClass];
                    })
                }
                athletesDivision={athletesDivision}
                athletesWeightClass={athletesWeightClass}
            />

            <div
                className="d-flex justify-content-between align-items-center mt-3"
                onClick={(e) => e.stopPropagation()}
            >
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                {error ? <FormHelperText error>{error} </FormHelperText> : null}
                <Stack direction={'row'} spacing={4}>
                    <Tooltip
                        title={
                            disableSubmit
                                ? selectWavier
                                    ? 'Each wrestler requires a division and weight class'
                                    : 'Please accept waiver'
                                : ''
                        }
                    >
                        <span>
                            <Box
                                minWidth="250px"
                                display="flex"
                                alignItems="center"
                                // position: relative;
                            >
                                <Checkbox
                                    onChange={(e) => setSelectWavier(!e.target.value)}
                                    value={selectWavier}
                                    checked={selectWavier}
                                >
                                    Accept
                                    <Button
                                        endIcon={
                                            <Launch style={{ height: '.8em', width: '.8em' }} />
                                        }
                                        onClick={() => setShowTermsModal(true)}
                                        sx={{ top: -1, textTransform: 'none' }}
                                        variant="text"
                                    >
                                        waiver
                                    </Button>
                                </Checkbox>
                                <FormHelperText error>
                                    {!selectWavier ? 'Please select waiver' : ' '}
                                </FormHelperText>
                            </Box>
                            <LoadingButton
                                disabled={disableSubmit}
                                loading={submitLoading}
                                onClick={handleBulkSubmit}
                                variant="contained"
                            >
                                Add Selected Wrestlers and pay
                            </LoadingButton>
                            {selectedRows.length ? (
                                <Alert severity="info" sx={{ marginTop: 1 }}>
                                    {`Registration Fee Total: $${selectedRows.length * event.fee}`}
                                </Alert>
                            ) : null}
                        </span>
                    </Tooltip>
                </Stack>
            </div>

            <Modal
                title={<span className="custom-heading">Waiver</span>}
                open={showTermsModal}
                footer={false}
                onCancel={() => setShowTermsModal(false)}
            >
                <div className="terms-modal">
                    {event && <div dangerouslySetInnerHTML={{ __html: event.waiver }}></div>}
                </div>
            </Modal>
        </>
    );
}
